/**
 * InfinitSales - Agent Activation Modal (Post-Onboarding)
 * Encourages new users to activate their agent right after onboarding.
 * Shows options: activate for all contacts, set schedule, or dismiss.
 */
import React, { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Bot, Zap, Clock, X } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const AgentActivationModal = ({ isOpen, onClose, onActivateAll, onSetSchedule, loading }) => {
  const { colors } = useTheme();

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  const modal = (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          backgroundColor: colors.bgPrimary,
          borderRadius: '20px',
          width: '100%',
          maxWidth: '420px',
          padding: '36px 32px 28px',
          border: `1px solid ${colors.borderColor}`,
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          textAlign: 'center',
          position: 'relative',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '14px',
            right: '14px',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '6px',
            borderRadius: '8px',
            color: colors.textTertiary,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <X size={18} />
        </button>

        {/* Icon */}
        <div
          style={{
            width: '64px',
            height: '64px',
            borderRadius: '16px',
            backgroundColor: `${colors.accentPrimary}15`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px',
          }}
        >
          <Bot size={32} color={colors.accentPrimary} />
        </div>

        {/* Title */}
        <h2
          style={{
            fontSize: '22px',
            fontWeight: 700,
            color: colors.textPrimary,
            margin: '0 0 8px',
          }}
        >
          Ative seu agente de IA
        </h2>

        {/* Subtitle */}
        <p
          style={{
            fontSize: '14px',
            color: colors.textSecondary,
            lineHeight: 1.5,
            margin: '0 0 28px',
            padding: '0 8px',
          }}
        >
          Seu agente está pronto para começar a atender seus contatos automaticamente.
        </p>

        {/* Buttons */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {/* Primary: Activate for all */}
          <button
            onClick={onActivateAll}
            disabled={loading}
            style={{
              width: '100%',
              padding: '13px 20px',
              backgroundColor: colors.accentPrimary,
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              opacity: loading ? 0.7 : 1,
              transition: 'opacity 0.15s',
            }}
          >
            <Zap size={18} />
            Ativar para todos os contatos
          </button>

          {/* Secondary: Set schedule */}
          <button
            onClick={onSetSchedule}
            style={{
              width: '100%',
              padding: '13px 20px',
              backgroundColor: `${colors.accentPrimary}10`,
              color: colors.accentPrimary,
              border: `1px solid ${colors.accentPrimary}30`,
              borderRadius: '12px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'all 0.15s',
            }}
          >
            <Clock size={18} />
            Programar horários específicos
          </button>

          {/* Dismiss */}
          <button
            onClick={onClose}
            style={{
              width: '100%',
              padding: '10px 20px',
              backgroundColor: 'transparent',
              color: colors.textTertiary,
              border: 'none',
              borderRadius: '12px',
              fontSize: '13px',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'color 0.15s',
            }}
          >
            Agora não
          </button>
        </div>

        {/* Footer note */}
        <p
          style={{
            fontSize: '11px',
            color: colors.textTertiary,
            marginTop: '16px',
            marginBottom: 0,
          }}
        >
          Você pode mudar isso depois nas configurações do agente
        </p>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default AgentActivationModal;
